import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "../imports";
import { AUTH_EVENTS, AUTH_CHANNEL_NAME } from "../constants/authConstants";

type ClientPrincipal = {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  userName?: string;
} | null;

type AuthContextType = {
  clientPrincipal: ClientPrincipal | null | undefined;
  isLoading: boolean;
};

const ClientPrincipalContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const useClientPrincipal = () => {
  const context = useContext(ClientPrincipalContext);
  if (context === undefined) {
    throw new Error(
      "useClientPrincipal must be used within a ClientPrincipalProvider",
    );
  }
  return context;
};

export const ClientPrincipalProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [clientPrincipal, setClientPrincipal] = useState<
    ClientPrincipal | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAuthStatus = useCallback(async () => {
    if (process.env.REACT_APP_USE_LOCAL_AUTH === "true") {
      const principal = {
        identityProvider: "local-test",
        userId: "test-user-id",
        userDetails: "testuser@cgi.com",
        userRoles: ["authenticated"],
        userName: "Test User",
      };

      setClientPrincipal(principal);
      sessionStorage.setItem("userOid", principal.userId);
      sessionStorage.setItem("userEmail", principal.userDetails.trim().toLowerCase());
      sessionStorage.setItem("userName", principal.userName);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch("/.auth/me");
      const data = await response.json();
      const principal = data?.clientPrincipal;

      if (!principal) {
        setClientPrincipal(null);
        setIsLoading(false);
        return;
      }

      const clean = (val?: string) => (val && !val.includes("*") ? val : "");
      const claim = (typ: string) =>
        clean(principal.claims?.find((c: { typ: string; val: string }) => c.typ === typ)?.val);

      const nameClaim      = claim("name");
      const givenName      = claim("given_name");
      const familyName     = claim("family_name");
      const preferredUser  = claim("preferred_username");
      const upn            = claim("upn");
      const emailPrefix    = clean(principal.userDetails?.split("@")[0]);

      const fullFromParts  = givenName && familyName
        ? `${givenName} ${familyName}`
        : givenName || familyName;

      const userName =
        nameClaim ||
        fullFromParts ||
        preferredUser?.split("@")[0] ||
        upn?.split("@")[0] ||
        emailPrefix ||
        "";

      const enrichedPrincipal = { ...principal, userName };

      setClientPrincipal(enrichedPrincipal);
      sessionStorage.setItem("userOid", principal.userId);
      sessionStorage.setItem("userEmail", (principal.userDetails || "").trim().toLowerCase());
      sessionStorage.setItem("userName", userName);
    } catch {
      setClientPrincipal(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuthStatus();
  }, [fetchAuthStatus]);

  useEffect(() => {
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      const authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);

      authChannel.onmessage = (event) => {
        switch (event.data.type) {
          case AUTH_EVENTS.LOGOUT:
            setClientPrincipal(null);
            setIsLoading(false);
            break;
          case AUTH_EVENTS.LOGIN:
          case AUTH_EVENTS.REFRESH:
            fetchAuthStatus();
            break;
        }
      };

      return () => {
        authChannel.close();
      };
    }
  }, [fetchAuthStatus]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "wasLoggedOut" && e.newValue === "true") {
        setClientPrincipal(null);
        setIsLoading(false);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchAuthStatus();
      }
    };

    const handleFocus = () => {
      fetchAuthStatus();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchAuthStatus]);

  const contextValue: AuthContextType = {
    clientPrincipal,
    isLoading,
  };

  return (
    <ClientPrincipalContext.Provider value={contextValue}>
      {children}
    </ClientPrincipalContext.Provider>
  );
};
